import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  fetchProducts,
  selectAllDresses,
  selectDressesLoading,
  selectPagination,
  selectCategories,
  selectCategoriesLoading,
  selectSubCategories,
  selectSubCategoriesLoading,
} from '../store/slices/dressesSlice';
import ProductGrid from '../components/ProductGrid';
import SubCategoryChips from '../components/SubCategoryChips';
import { selectListPage, setListPage } from '../store/slices/filtersSlice';
import { pushViewItemList } from '../utils/gtm';

const PAGE_SIZE = 12;

const SubCategoryPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const products = useSelector(selectAllDresses);
  const loading = useSelector(selectDressesLoading);
  const pagination = useSelector(selectPagination);
  const categories = useSelector(selectCategories);
  const categoriesLoading = useSelector(selectCategoriesLoading);
  const subCategories = useSelector(selectSubCategories);
  const subCategoriesLoading = useSelector(selectSubCategoriesLoading);

  const listKey = `subcategory:${id}`;
  const page = useSelector(state => selectListPage(state, listKey));
  const setPage = (p) => dispatch(setListPage({ key: listKey, page: p }));

  const subCategory = subCategories.find(sc => String(sc.id) === String(id));
  const parentCategory = subCategory
    ? categories.find(c => String(c.id) === String(subCategory.categoryId))
    : null;
  const namesLoading = categoriesLoading || subCategoriesLoading;
  const subCategoryName = subCategory?.name || (namesLoading ? '' : 'Sub-Category');
  const siblingSubCategories = parentCategory
    ? subCategories.filter(sc => String(sc.categoryId) === String(parentCategory.id))
    : [];

  useEffect(() => {
    dispatch(fetchProducts({
      subCategoryId: id,
      page,
      pageSize: PAGE_SIZE,
    }));
  }, [dispatch, id, page]);

  const totalPages = Math.ceil(pagination.count / PAGE_SIZE);

  // GA4 view_item_list — fires whenever a new page of this sub-category's products loads
  useEffect(() => {
    if (!loading && !namesLoading && products.length > 0) {
      pushViewItemList(products, {
        listId: id,
        listName: subCategoryName,
        page,
        pageSize: PAGE_SIZE,
      });
    }
  }, [products, loading, namesLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-primary-600 transition-colors">Store</Link>
        <span>/</span>
        {parentCategory && (
          <>
            <Link to={`/category/${parentCategory.id}`} className="text-gray-400 hover:text-primary-600 transition-colors">
              {parentCategory.name}
            </Link>
            <span>/</span>
          </>
        )}
        <span className="text-gray-900 font-medium">{subCategoryName}</span>
      </nav>

      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">{subCategoryName}</h1>
        {!loading && (
          <p className="text-sm text-gray-500 mt-0.5">
            {pagination.count > 0
              ? `${pagination.count.toLocaleString()} product${pagination.count !== 1 ? 's' : ''}`
              : 'No products found'}
          </p>
        )}
      </div>

      {!namesLoading && parentCategory && (
        <SubCategoryChips
          categoryId={parentCategory.id}
          subCategories={siblingSubCategories}
          activeSubCategoryId={id}
        />
      )}

      <ProductGrid
        products={products}
        loading={loading}
        listId={id}
        listName={subCategoryName}
        startIndex={(page - 1) * PAGE_SIZE}
      />

      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Prev</span>
          </button>
          <span className="text-sm text-gray-500 px-2">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default SubCategoryPage;
